'use client';

import { Button, ButtonGroup, Flex, Input } from '@/_components';
import {
  type ChangeEvent,
  type FC,
  type FormEvent,
  useContext,
  useState,
} from 'react';
import { LoginContext } from '@/_contexts/LoginProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { sendPasswordResetEmail } from '@/_api/auth';

export const ForgotPassword: FC<{}> = () => {
  const { setLoginState, setUsername, username } = useContext(LoginContext);
  const [email, setEmail] = useState<string>(username);
  const { addToast } = useContext(ToastContext);

  const onBack = () => setLoginState('login');

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value?.trim()?.toLowerCase());

  const onFormSubmit = async (event: FormEvent) => {
    event?.preventDefault();

    if (!email) {
      return;
    }

    try {
      setUsername(email);

      // Try WorkOS password reset first
      try {
        const response = await sendPasswordResetEmail({
          email: email.trim(),
        });

        if (response?.workos_enabled) {
          addToast('success', response.message);
          setLoginState('sent-link');
          return;
        } else if (response?.error) {
          addToast('error', response.error);
          return;
        }
      } catch (workosError: any) {
        // Fall back to Firebase if WorkOS fails or is not enabled
        if (workosError?.status !== 400) {
          console.warn(
            'WorkOS password reset failed, falling back to Firebase',
          );
        }
      }

      setLoginState('sent-link');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  return (
    <form onSubmit={onFormSubmit}>
      <Flex isColumn={true} gap={8} padding={10}>
        <Input
          label="Your email"
          value={email}
          onChange={onEmailChange}
          type="email"
          placeholder="Enter your email"
        />

        <ButtonGroup align="space-between" className="flex-row">
          <Button
            iconLeft="arrow-left"
            value="Back"
            type="button"
            onClick={onBack}
            intent="ghost"
          />

          <Button value="Send reset link" type="submit" intent="primary" />
        </ButtonGroup>
      </Flex>
    </form>
  );
};
